import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { type FAQ, ensureFAQs, titleCase, type PageType } from "@/lib/seo";


interface SeoFAQProps {
  primaryKeyword: string;
  faqs?: FAQ[];
  type?: PageType;
  className?: string;
  /** Plain-English H2. Defaults to "<Keyword> FAQs" for pages that haven't set one. */
  heading?: string;
}

/**
 * Shared FAQ section.
 *
 * Only renders questions the page actually answers: no generic filler is
 * appended, and a page with no questions renders nothing at all (so no empty
 * FAQPage schema is published either).
 */
export function SeoFAQ({
  primaryKeyword,
  faqs = [],
  type = "service",
  className,
  heading: headingProp,
}: SeoFAQProps) {
  const ensured = ensureFAQs(primaryKeyword, faqs, type);
  if (ensured.length === 0) return null;
  const heading = headingProp ?? `${titleCase(primaryKeyword)} FAQs`;

  return (
    <section
      className={
        className ??
        "relative overflow-hidden bg-gradient-to-b from-card/30 via-background to-card/40 px-6 py-24 lg:px-8"
      }
      aria-labelledby="faq-heading"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_color-mix(in_oklab,var(--brand)_12%,transparent),transparent_60%)]"
      />
      <div className="relative mx-auto max-w-6xl">
        <div className="mb-14 max-w-2xl">
          <span className="inline-flex items-center rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Frequently asked
          </span>
          <h2 id="faq-heading" className="mt-4 font-serif text-4xl leading-tight text-brand sm:text-5xl">
            {heading}
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Everything you need to know, answered in full, no clicks required.
          </p>
        </div>

        <Accordion type="single" collapsible className="space-y-3">
          {ensured.map((f, i) => (
            <AccordionItem
              key={f.question}
              value={`faq-${i}`}
              className="rounded-xl border border-border bg-card px-6 shadow-sm"
            >
              <AccordionTrigger className="py-5 text-base font-medium text-brand hover:no-underline">
                {f.question}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-sm leading-relaxed text-muted-foreground">
                <p className="whitespace-pre-line">{f.answer}</p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
